import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/onboard.css';
import OnboardLeftSection from '../components/OnboardLeftSection';

function ResetPassword({ onClose, onSwitchToSignIn }) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [token, setToken] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const tokenFromUrl = searchParams.get('token');
        if (!tokenFromUrl) {
            setError('Invalid reset link. Please request a new password reset.');
        } else {
            setToken(tokenFromUrl);
        }
    }, [searchParams]);

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (error) setError('');
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Validation
        if (!password || !confirmPassword) {
            setError('Please fill in all fields');
            setLoading(false);
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        if (!token) {
            setError('Invalid reset link');
            setLoading(false);
            return;
        }

        try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

            const response = await fetch(`${supabaseUrl}/functions/v1/verify-reset-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${anonKey}`
                },
                body: JSON.stringify({
                    token: token,
                    newPassword: password
                })
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.error || 'Failed to reset password');
            }

            setSuccess(true);

            // Redirect to sign in after 3 seconds
            setTimeout(() => {
                if (onSwitchToSignIn) {
                    onSwitchToSignIn();
                } else {
                    navigate('/?view=signin');
                }
            }, 3000);

        } catch (err) {
            // Don't log to console for better UX - just show user-friendly message
            // Determine appropriate error message based on error type
            let errorMessage = 'Failed to reset password. Please try again or request a new reset link.';

            if (err.message) {
                if (err.message.includes('expired') || err.message.includes('Invalid')) {
                    errorMessage = 'This reset link has expired or is invalid. Please request a new password reset.';
                } else if (err.message.includes('already been used')) {
                    errorMessage = 'This reset link has already been used. Please request a new password reset if needed.';
                } else if (err.message.includes('Password must be')) {
                    errorMessage = err.message; // Show password requirement errors directly
                } else if (err.message.includes('network')) {
                    errorMessage = 'Network error. Please check your connection and try again.';
                }
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSignInClick = () => {
        if (onSwitchToSignIn) {
            onSwitchToSignIn();
        } else {
            navigate('/?view=signin');
        }
    };

    if (success) {
        return (
            <div className="onboard-container signin-container">
                <div className="signin-layout">
                    <OnboardLeftSection
                        onClose={onClose}
                        loading={false}
                        mode="resetPassword"
                    />

                    <div className="signin-content">
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                margin: '0 auto 30px',
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '40px'
                            }}>
                                ✓
                            </div>
                            <h1>Password Reset Successfully!</h1>
                            <p className="signin-subtitle" style={{ maxWidth: '500px', margin: '20px auto' }}>
                                Your password has been changed successfully. You can now sign in with your new password.
                            </p>
                            <p style={{ color: '#666', marginTop: '20px', fontSize: '14px' }}>
                                Redirecting you to sign in...
                            </p>

                            <button
                                onClick={handleSignInClick}
                                className="signin-btn"
                                style={{ marginTop: '30px', maxWidth: '300px' }}
                            >
                                Sign In Now
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="onboard-container signin-container">
            <div className="signin-layout">
                <OnboardLeftSection
                    onClose={onClose}
                    loading={loading}
                    mode="resetPassword"
                />

                <div className="signin-content">
                    <h1>Create New Password</h1>
                    <p className="signin-subtitle">
                        Enter your new password below
                    </p>

                    <form className="signin-form" onSubmit={handleSubmit}>
                        {error && (
                            <div className="error-message" style={{
                                color: '#e74c3c',
                                backgroundColor: '#fdf2f2',
                                padding: '10px',
                                borderRadius: '4px',
                                marginBottom: '15px',
                                border: '1px solid #fecaca'
                            }}>
                                {error}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="password">New Password</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder="Enter new password (min. 8 characters)"
                                required
                                autoFocus
                            />
                            <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                Must be at least 8 characters long
                            </small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm New Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                                placeholder="Confirm your new password"
                                required
                            />
                        </div>

                        <button type="submit" className="signin-btn" disabled={loading || !token}>
                            {loading ? 'Resetting Password...' : 'Reset Password'}
                        </button>
                    </form>

                    <div className="signin-footer">
                        <p>
                            Remember your password?{' '}
                            <span className="signup-link" onClick={handleSignInClick}>
                                Sign in here
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
